import { useState, useEffect, useCallback } from 'react';
import { Loader2 } from 'lucide-react';
import { ACCENT, OK, BAD, WARN, VIOLET, tableSurfaces } from '../lib/uiTokens';
import DphStatistikyPanel from '../components/finance/DphStatistikyPanel';

const MESICE = ['Leden','Únor','Březen','Duben','Květen','Červen','Červenec','Srpen','Září','Říjen','Listopad','Prosinec'];

const fmt = (v) => {
    if (v == null) return '—';
    return new Intl.NumberFormat('cs-CZ', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v);
};

/**
 * Sloupce hlavní tabulky per režim. `g` = skupina (spojená hlavička nad sloupci),
 * `l` = popisek sloupce, `get` = čtečka hodnoty z měsíčního záznamu, `c` = barva,
 * `b` = tučně (souhrnné sloupce).
 *
 * Barvy sazeb drží konvenci z uiTokens: 12 % zelená (OK), 21 % fialová (VIOLET).
 */
const SLOUPCE = {
    souhrn: [
        { g: 'DPH na výstupu', l: '12 %',   get: r => r.vystup?.dph_12,      c: OK },
        { g: 'DPH na výstupu', l: '21 %',   get: r => r.vystup?.dph_21,      c: VIOLET },
        { g: 'DPH na výstupu', l: 'Celkem', get: r => r.dph_vystup_celkem,   c: ACCENT, b: true },
        { g: 'DPH na vstupu',  l: '12 %',   get: r => r.vstup?.dph_12,       c: OK },
        { g: 'DPH na vstupu',  l: '21 %',   get: r => r.vstup?.dph_21,       c: VIOLET },
        { g: 'DPH na vstupu',  l: 'Celkem', get: r => r.dph_vstup_celkem,    c: OK, b: true },
        { g: '',               l: 'K odvodu', get: r => r.dph_k_odvodu,      c: WARN, b: true },
    ],
    vystup: [
        { g: 'Sazba 12 %', l: 'Základ', get: r => r.vystup?.zaklad_12 },
        { g: 'Sazba 12 %', l: 'DPH',    get: r => r.vystup?.dph_12,     c: OK },
        { g: 'Sazba 21 %', l: 'Základ', get: r => r.vystup?.zaklad_21 },
        { g: 'Sazba 21 %', l: 'DPH',    get: r => r.vystup?.dph_21,     c: VIOLET },
        { g: 'Celkem',     l: 'Základ', get: r => r.vystup?.zaklad_celkem, b: true },
        { g: 'Celkem',     l: 'DPH',    get: r => r.vystup?.dph_celkem, c: ACCENT, b: true },
    ],
    vstup: [
        { g: 'Bez DPH',    l: '0 %',    get: r => r.vstup?.zaklad_0 },
        { g: 'Sazba 12 %', l: 'Základ', get: r => r.vstup?.zaklad_12 },
        { g: 'Sazba 12 %', l: 'DPH',    get: r => r.vstup?.dph_12,      c: OK },
        { g: 'Sazba 21 %', l: 'Základ', get: r => r.vstup?.zaklad_21 },
        { g: 'Sazba 21 %', l: 'DPH',    get: r => r.vstup?.dph_21,      c: VIOLET },
        { g: 'Celkem',     l: 'Základ', get: r => r.vstup?.zaklad_celkem, b: true },
        { g: 'Celkem',     l: 'DPH',    get: r => r.vstup?.dph_celkem,  c: OK, b: true },
    ],
};

const REZIMY = [
    { id: 'souhrn', label: 'Souhrn' },
    { id: 'vystup', label: 'Výstup (tržby)' },
    { id: 'vstup',  label: 'Vstup (doklady)' },
];

export default function FinanceDph({ rok, mesic, cardStyle, isDark }) {
    const [data, setData] = useState(null);
    const [rocniData, setRocniData] = useState([]);
    const [dodavatele, setDodavatele] = useState(null);
    const [loading, setLoading] = useState(true);
    const [ukladani, setUkladani] = useState(false);
    const [rezim, setRezim] = useState('souhrn');
    const [obdobiDod, setObdobiDod] = useState('mesic'); // mesic | rok

    const token = document.querySelector('meta[name="csrf-token"]')?.content;

    const loadData = useCallback(() => {
        setLoading(true);
        Promise.all([
            fetch(`/api/finance/dph?rok=${rok}&mesic=${mesic}`).then(r => r.json()),
            fetch(`/api/finance/dph?rok=${rok}`).then(r => r.json()),
        ]).then(([d, r]) => {
            setData(d);
            setRocniData(Array.isArray(r) ? r : []);
            setLoading(false);
        }).catch(() => setLoading(false));
    }, [rok, mesic]);

    useEffect(() => { loadData(); }, [loadData]);

    // Dodavatelé — samostatný dotaz, má vlastní přepínač období (měsíc / celý rok)
    useEffect(() => {
        const p = new URLSearchParams({ rok });
        if (obdobiDod === 'mesic') p.set('mesic', mesic);
        setDodavatele(null);
        fetch(`/api/finance/dph/dodavatele?${p.toString()}`)
            .then(r => r.json()).then(setDodavatele).catch(() => setDodavatele({ radky: [] }));
    }, [rok, mesic, obdobiDod]);

    const handleZaplaceno = async (datumZaplaceni) => {
        setUkladani(true);
        try {
            await fetch(`/api/finance/dph/${rok}/${mesic}/zaplaceno`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': token },
                body: JSON.stringify({ datum_zaplaceni: datumZaplaceni }),
            });
            loadData();
        } catch (e) { alert('Chyba při ukládání'); }
        setUkladani(false);
    };

    if (loading) {
        return <div className="flex items-center justify-center py-20"><Loader2 size={24} className="animate-spin" style={{ color: ACCENT }} /></div>;
    }
    if (!data) return <div className="text-center py-10" style={{ color: 'var(--text-tertiary)' }}>Nepodařilo se načíst DPH data</div>;

    // Od xl výš stránka neroluje — vyplní výšku a scrollují jen oba sloupce zvlášť
    // (scrollbary jsou globálně skryté, takže to nedělá dva pruhy vedle sebe).
    // Pod xl se sloupce zalomí pod sebe a roluje se normálně celá stránka.
    return (
        <div className="flex flex-col xl:flex-row gap-3 items-start xl:h-full xl:min-h-0 xl:items-stretch">
            {/* Hlavní plocha — tabulky rozpadů */}
            <div className="flex-1 min-w-0 w-full flex flex-col gap-3 xl:min-h-0 xl:overflow-y-auto">
                <RozpadTabulka
                    rocniData={rocniData} rok={rok} mesic={mesic}
                    rezim={rezim} setRezim={setRezim}
                    cardStyle={cardStyle} isDark={isDark}
                />
                <DodavateleTabulka
                    dodavatele={dodavatele} rok={rok} mesic={mesic}
                    obdobi={obdobiDod} setObdobi={setObdobiDod}
                    cardStyle={cardStyle} isDark={isDark}
                />
            </div>

            {/* Pravý panel statistik — vejde se na výšku, neroluje (graf dobere
                zbytek). Pod xl se zalomí pod tabulky. */}
            <div className="w-full xl:w-[clamp(340px,26vw,460px)] xl:flex-shrink-0 xl:min-h-0">
                <DphStatistikyPanel
                    data={data} rocniData={rocniData} rok={rok} mesic={mesic}
                    cardStyle={cardStyle} onZaplaceno={handleZaplaceno} ukladani={ukladani}
                />
            </div>
        </div>
    );
}

// ── Hlavní tabulka — měsíce × rozpad podle sazeb ─────────────────────────────

function RozpadTabulka({ rocniData, rok, mesic, rezim, setRezim, cardStyle, isDark }) {
    const surf = tableSurfaces(isDark);
    const sloupce = SLOUPCE[rezim];
    const seStavem = rezim === 'souhrn';

    // Spojené hlavičky nad sloupci — sousední sloupce se stejnou skupinou se slijí.
    const skupiny = [];
    sloupce.forEach(s => {
        const posl = skupiny[skupiny.length - 1];
        if (posl && posl.g === s.g) posl.span += 1;
        else skupiny.push({ g: s.g, span: 1 });
    });

    const soucet = (get) => rocniData.reduce((s, r) => s + (Number(get(r)) || 0), 0);

    return (
        <div className="rounded-2xl p-5 flex-shrink-0" style={cardStyle}>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                <h3 className="text-xs font-bold uppercase tracking-wide" style={{ color: 'var(--text-secondary)' }}>
                    Rozpad DPH podle sazeb — {rok}
                </h3>
                <div className="flex items-center gap-1 rounded-lg p-0.5" style={{ background: surf.headBg }}>
                    {REZIMY.map(r => (
                        <button key={r.id} onClick={() => setRezim(r.id)}
                            className="px-3 py-1 rounded-md text-xs font-semibold transition-colors"
                            style={{
                                background: rezim === r.id ? ACCENT : 'transparent',
                                color: rezim === r.id ? '#fff' : 'var(--text-secondary)',
                            }}>
                            {r.label}
                        </button>
                    ))}
                </div>
            </div>

            <div className="overflow-x-auto">
                <table className="w-full" style={{ borderCollapse: 'collapse' }}>
                    <thead>
                        {/* Skupinová hlavička — nad sloupci drží kontext (výstup / vstup / sazba) */}
                        <tr style={{ background: surf.groupBg }}>
                            <th />
                            {skupiny.map((sk, i) => (
                                <th key={i} colSpan={sk.span}
                                    className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wide text-center"
                                    style={{ color: 'var(--text-secondary)', borderLeft: i > 0 ? '1px solid var(--border-subtle)' : 'none' }}>
                                    {sk.g}
                                </th>
                            ))}
                            {seStavem && <th />}
                        </tr>
                        <tr style={{ background: surf.headBg }}>
                            <Th align="left">Měsíc</Th>
                            {sloupce.map((s, i) => <Th key={i}>{s.l}</Th>)}
                            {seStavem && <Th align="center">Stav</Th>}
                        </tr>
                    </thead>
                    <tbody>
                        {rocniData.map((rd, i) => {
                            const aktualni = rd.mesic === mesic;
                            return (
                                <tr key={i} style={{ borderBottom: '1px solid var(--border-subtle)', background: aktualni ? surf.strongBg : 'transparent' }}>
                                    <td className="px-3 py-2 text-[13px] whitespace-nowrap"
                                        style={{ color: 'var(--text-primary)', fontWeight: aktualni ? 700 : 500 }}>
                                        {MESICE[rd.mesic - 1]}
                                    </td>
                                    {sloupce.map((s, j) => (
                                        <Num key={j} v={fmt(s.get(rd))} color={s.c} bold={s.b} />
                                    ))}
                                    {seStavem && (
                                        <td className="px-3 py-2 text-center">
                                            <StavBadge stav={rd.stav_odvodu} />
                                        </td>
                                    )}
                                </tr>
                            );
                        })}
                        {rocniData.length === 0 && (
                            <tr><td colSpan={sloupce.length + 2} className="text-center py-6" style={{ color: 'var(--text-tertiary)' }}>Žádná data</td></tr>
                        )}
                        {rocniData.length > 0 && (
                            <tr style={{ background: surf.strongBg }}>
                                <td className="px-3 py-2 text-[13px] font-bold whitespace-nowrap" style={{ color: 'var(--text-primary)' }}>YTD celkem</td>
                                {sloupce.map((s, j) => (
                                    <Num key={j} v={fmt(soucet(s.get))} color={s.c} bold />
                                ))}
                                {seStavem && <td />}
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
            <div className="text-[10.5px] mt-2" style={{ color: 'var(--text-muted)' }}>
                Výstup = tržby (stg_trzby), vstup = schválené doklady. Hodnoty v Kč zaokrouhlené na celé koruny.
            </div>
        </div>
    );
}

// ── Dodavatelé — na kterých dodavatelích kolik DPH na vstupu ─────────────────

function DodavateleTabulka({ dodavatele, rok, mesic, obdobi, setObdobi, cardStyle, isDark }) {
    const surf = tableSurfaces(isDark);
    const radky = dodavatele?.radky || [];
    const celkem = dodavatele?.dph_celkem || 0;
    const max = Math.max(1, ...radky.map(r => Math.abs(r.dph_celkem)));

    return (
        <div className="rounded-2xl p-5 flex-shrink-0" style={cardStyle}>
            <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                <h3 className="text-xs font-bold uppercase tracking-wide" style={{ color: 'var(--text-secondary)' }}>
                    DPH na vstupu podle dodavatelů
                </h3>
                <div className="flex items-center gap-1 rounded-lg p-0.5" style={{ background: surf.headBg }}>
                    {[{ id: 'mesic', label: MESICE[mesic - 1] }, { id: 'rok', label: `Rok ${rok}` }].map(o => (
                        <button key={o.id} onClick={() => setObdobi(o.id)}
                            className="px-3 py-1 rounded-md text-xs font-semibold transition-colors"
                            style={{
                                background: obdobi === o.id ? ACCENT : 'transparent',
                                color: obdobi === o.id ? '#fff' : 'var(--text-secondary)',
                            }}>
                            {o.label}
                        </button>
                    ))}
                </div>
            </div>

            {!dodavatele ? (
                <div className="py-8 text-center"><Loader2 size={18} className="animate-spin inline" style={{ color: ACCENT }} /></div>
            ) : (
                <div className="overflow-x-auto">
                    <table className="w-full" style={{ borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ background: surf.headBg }}>
                                <Th align="left">Dodavatel</Th>
                                <Th>Dokladů</Th>
                                <Th>Celkem s DPH</Th>
                                <Th>Bez DPH (0 %)</Th>
                                <Th>DPH 12 %</Th>
                                <Th>DPH 21 %</Th>
                                <Th>DPH celkem</Th>
                                <Th align="left">Podíl</Th>
                            </tr>
                        </thead>
                        <tbody>
                            {radky.map((r, i) => {
                                const podil = celkem > 0 ? (r.dph_celkem / celkem) * 100 : 0;
                                return (
                                    <tr key={i} style={{ borderBottom: '1px solid var(--border-subtle)' }}>
                                        <td className="px-3 py-2 text-[13px] max-w-[240px] truncate" title={r.nazev}
                                            style={{ color: r.dodavatel_id ? 'var(--text-primary)' : 'var(--text-tertiary)' }}>
                                            {r.nazev}
                                        </td>
                                        <Num v={r.pocet} />
                                        <Num v={fmt(r.castka_s_dph)} />
                                        <Num v={fmt(r.zaklad_0)} color="var(--text-tertiary)" />
                                        <Num v={fmt(r.dph_12)} color={OK} />
                                        <Num v={fmt(r.dph_21)} color={VIOLET} />
                                        <Num v={fmt(r.dph_celkem)} bold />
                                        <td className="px-3 py-2" style={{ minWidth: 120 }}>
                                            <div className="flex items-center gap-2">
                                                <div className="flex-1" style={{ height: 6, borderRadius: 2, background: 'var(--bg-elevated)' }}>
                                                    <div style={{ width: `${Math.max(2, Math.abs(r.dph_celkem) / max * 100)}%`, height: 6, borderRadius: 2, background: ACCENT }} />
                                                </div>
                                                <span className="tabular-nums text-[11px] w-10 text-right" style={{ color: 'var(--text-muted)' }}>
                                                    {podil.toFixed(0)} %
                                                </span>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                            {radky.length === 0 && (
                                <tr><td colSpan={8} className="text-center py-6" style={{ color: 'var(--text-tertiary)' }}>Žádné doklady za období</td></tr>
                            )}
                            {radky.length > 0 && (
                                <tr style={{ background: surf.strongBg }}>
                                    <td className="px-3 py-2 text-[13px] font-bold" style={{ color: 'var(--text-primary)' }}>
                                        Celkem ({radky.length} dodavatelů)
                                    </td>
                                    <Num v={radky.reduce((s, r) => s + r.pocet, 0)} bold />
                                    <Num v={fmt(radky.reduce((s, r) => s + r.castka_s_dph, 0))} bold />
                                    <Num v={fmt(radky.reduce((s, r) => s + r.zaklad_0, 0))} bold />
                                    <Num v={fmt(radky.reduce((s, r) => s + r.dph_12, 0))} color={OK} bold />
                                    <Num v={fmt(radky.reduce((s, r) => s + r.dph_21, 0))} color={VIOLET} bold />
                                    <Num v={fmt(celkem)} bold />
                                    <td />
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

// ── Sdílené prvky dle normy Výsledovky ───────────────────────────────────────

const Th = ({ children, align = 'right' }) => (
    <th className={`px-3 py-2.5 text-[10.5px] font-bold uppercase tracking-wide whitespace-nowrap ${align === 'left' ? 'text-left' : align === 'center' ? 'text-center' : 'text-right'}`}
        style={{ color: 'var(--text-tertiary)' }}>{children}</th>
);

const Num = ({ v, color, bold }) => (
    <td className="px-3 py-2 text-right tabular-nums text-[12.5px] whitespace-nowrap"
        style={{ color: color || 'var(--text-primary)', fontWeight: bold ? 700 : 400 }}>{v}</td>
);

const StavBadge = ({ stav }) => {
    const zaplaceno = stav === 'zaplaceno';
    const barva = zaplaceno ? OK : BAD;
    return (
        <span style={{
            fontSize: 9, fontWeight: 700, letterSpacing: '0.04em', padding: '2px 7px', borderRadius: 4,
            background: `${barva}1f`, color: barva, whiteSpace: 'nowrap',
        }}>
            {zaplaceno ? 'ZAPLACENO' : 'NEZAPLACENO'}
        </span>
    );
};
